import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { createBloodGlucoseMeasurement } from "~/api/bloodGlucose";
import { errorToast, useToastStore } from "~/components/Shared/Toast";
import { Button, Input, Label, Modal } from "~/ui";

interface BloodGlucoseInputModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: number;
}

export const BloodGlucoseInputModal = ({
  isOpen,
  onClose,
  patientId,
}: BloodGlucoseInputModalProps) => {
  const { t } = useTranslation();
  const { pushToast } = useToastStore();
  const queryClient = useQueryClient();

  // Helper function to get local datetime string for input
  const getLocalDateTimeString = (date: Date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [formData, setFormData] = useState({
    value: "",
    measured_at: getLocalDateTimeString(), // Format: YYYY-MM-DDTHH:MM in local timezone
  });

  const { mutate: createMeasurement, isPending } = useMutation({
    mutationFn: createBloodGlucoseMeasurement,
    onSuccess: async () => {
      // Invalidate relevant queries to refresh data
      await queryClient.invalidateQueries({
        queryKey: ["getPatient", { id: patientId }],
      });
      await queryClient.invalidateQueries({
        queryKey: ["lastMeasurements", patientId],
      });

      // Show success toast
      void pushToast({
        type: "success",
        message: "Blood glucose measurement added successfully!",
      });

      // Reset form
      setFormData({
        value: "",
        measured_at: getLocalDateTimeString(),
      });

      // Close modal after a short delay to ensure toast is visible
      setTimeout(() => {
        onClose();
      }, 1000);
    },
    onError: (err) => {
      errorToast(err);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const value = parseFloat(formData.value);
    if (isNaN(value) || value < 30) {
      void pushToast({
        type: "error",
        message: "Blood glucose must be at least 30 mg/dL",
      });
      return;
    }

    // Convert local datetime to the expected format (YYYY-MM-DDTHH:mm:ssZ)
    // The input is in local timezone, convert to UTC for API
    const localDate = new Date(formData.measured_at);
    const measuredAt = localDate.toISOString().replace(/\.\d{3}Z$/, "Z");

    createMeasurement({
      patient_id: patientId,
      value,
      measured_at: measuredAt,
    });
  };

  const handleReset = () => {
    setFormData({
      value: "",
      measured_at: getLocalDateTimeString(),
    });
  };

  const isFormValid = () => {
    const value = parseFloat(formData.value);
    return !isNaN(value) && value >= 30 && formData.value.trim() !== "";
  };

  const handleInputChange =
    (field: keyof typeof formData) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: e.target.value,
      }));
    };

  return (
    <Modal
      title="Add Blood Glucose"
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="value" className="text-sm font-medium text-gray-200">
            Blood Glucose Value ({t("health.mg_dl")}) - Min: 30 mg/dL
          </Label>
          <Input
            id="value"
            type="number"
            step="0.1"
            min="30"
            value={formData.value}
            onChange={handleInputChange("value")}
            placeholder="Enter blood glucose in mg/dL (minimum 30)"
            className="mt-1"
            required
          />
        </div>

        <div>
          <Label
            htmlFor="measured_at"
            className="text-sm font-medium text-gray-200"
          >
            Measured At
          </Label>
          <Input
            id="measured_at"
            type="datetime-local"
            value={formData.measured_at}
            onChange={handleInputChange("measured_at")}
            className="mt-1"
            required
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <Button
            type="button"
            text="Reset"
            color="secondary"
            onClick={handleReset}
            disabled={isPending}
          />
          <Button
            type="submit"
            text={isPending ? "Saving..." : "Save"}
            color="primary"
            disabled={isPending || !isFormValid()}
          />
        </div>
      </form>
    </Modal>
  );
};
